import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Alert,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { AuthContext } from "../../context/AuthContext";
import WaveformIcon from "../viewers/WaveformIcon";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { userLogin } from "../../services/api";

const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup
    .string()
    .min(3, "Minimum 3 characters")
    .required("Password is required"),
});

export default function Login() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [serverError, setServerError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    setServerError("");
    setIsSubmitting(true);

    try {
      const userData = await userLogin(data.email, data.password);
      await login(userData); // Pass full user data to context

      navigate("/");
    } catch (error) {
      setServerError(error.message);
      if (error.message.includes("Invalid")) {
        setError("email", { type: "manual", message: error.message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#0d0d0d",
        // backgroundImage: "url('/bgImage.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        p: 1,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <Paper
        elevation={12}
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          maxWidth: 750,
          width: "100%",
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: "rgba(18,18,18,0.9)", // semi-transparent over bg
          color: "#eee",
        }}
      >
        {/* Left side */}
        <Box
          sx={{
            flex: 1,
            background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: isSmallScreen ? 3 : 4,
            textAlign: "center",
          }}
        >
          <Box
            sx={{ display: "inline-flex", alignItems: "center", gap: 1, mb: 1 }}
          >
          <Typography
            variant={isSmallScreen ? "h5" : "h4"}
            sx={{
              fontWeight: "800",
              mb: 1,
              letterSpacing: "0.12em",
              textShadow: "0 0 10px rgba(255,255,255,0.3)",
            }}
          >
            Tatva AI
          </Typography>
           <Box
              component="span"
              sx={{
                fontSize: "0.55rem",
                fontWeight: 700,
                color: "#fcd34d",
                bgcolor: "#334155",
                borderRadius: "3px",
                px: 0.5,
                py: 0.25,
                mb: 3,
                ml:-1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                animation: "float 2s ease-in-out infinite",
                "@keyframes float": {
                  "0%,100%": { transform: "translateY(0)" },
                  "50%": { transform: "translateY(-7px)" },
                },
              }}
            >
              beta
            </Box>
          </Box>
          <Typography
            variant={isSmallScreen ? "body2" : "body1"}
            sx={{
              mb: 3,
              fontWeight: "400",
              opacity: 0.85,
              maxWidth: 260,
              mx: "auto",
              lineHeight: 1.4,
            }}
          >
            Welcome back! Upload your data files and get instant AI-powered
            insights.
          </Typography>
          <WaveformIcon width={100} height={70} color="#000" lines={15} />
        </Box>

        {/* Right side */}
        <Box
          sx={{
            flex: 1,
            p: isSmallScreen ? 3 : 4,
            backgroundColor: "#121212",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant={isSmallScreen ? "h6" : "h5"}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: "600",
              mb: 3,
              textAlign: isSmallScreen ? "center" : "left",
            }}
          >
            Login to Tatva AI
          </Typography>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            {serverError && (
              <Alert
                severity="error"
                sx={{ mb: 1.5, fontSize: "0.8rem", py: 0.5 }}
              >
                {serverError}
              </Alert>
            )}

            {[
              {
                name: "email",
                label: "Email Address",
                icon: <EmailIcon sx={{ color: "#8e8e8e" }} />,
                type: "email",
              },
              {
                name: "password",
                label: "Password",
                icon: <LockIcon sx={{ color: "#8e8e8e" }} />,
                type: "password",
              },
            ].map(({ name, label, icon, type }) => (
              <TextField
                key={name}
                label={label}
                type={type}
                fullWidth
                margin="dense"
                {...register(name)}
                error={!!errors[name]}
                helperText={errors[name]?.message}
                variant="filled"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{icon}</InputAdornment>
                  ),
                  sx: {
                    backgroundColor: "#2a2a2a",
                    color: "#eee",
                    borderRadius: 2,
                    "& .MuiInputBase-input": {
                      color: "#eee",
                      "&:-webkit-autofill": {
                        WebkitBoxShadow:
                          "0 0 0 1000px #2a2a2a inset !important",
                        WebkitTextFillColor: "#eee !important",
                        borderRadius: "8px",
                        transition: "background-color 5000s ease-in-out 0s",
                      },
                      "&:-webkit-autofill:hover": {
                        WebkitBoxShadow:
                          "0 0 0 1000px #3a3a3a inset !important",
                      },
                      "&:-webkit-autofill:focus": {
                        WebkitBoxShadow:
                          "0 0 0 1000px #3a3a3a inset !important",
                      },
                    },
                    "& .MuiFormHelperText-root": { color: "#ff6b6b" },
                    transition:
                      "background-color 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": { backgroundColor: "#3a3a3a" },
                    "&.Mui-focused": {
                      backgroundColor: "#3a3a3a",
                      boxShadow: "0 0 12px 3px #764ba2",
                    },
                  },
                }}
              />
            ))}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              sx={{
                mt: 3,
                py: 1.2,
                fontSize: "0.95rem",
                borderRadius: 2,
                background: "linear-gradient(90deg, #764ba2, #667eea)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #5a3e9e 0%, #5a67d8 100%)",
                },
              }}
            >
              {isSubmitting ? "Logging in..." : "Login"}
            </Button>
            <Typography
              variant="body2"
              sx={{
                mt: 2.5,
                textAlign: "center",
                color: "#bbb",
                fontSize: "0.8rem",
              }}
            >
              Don't have an account?{" "}
              <Link
                component={RouterLink}
                to="/signup"
                underline="hover"
                sx={{
                  color: "#8e8e8e",
                  fontWeight: "600",
                  "&:hover": { color: "#667eea" },
                }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
